import React, { useState } from 'react';
import Joyride, {
    STATUS,
    ACTIONS,
    LIFECYCLE,
    EVENTS,
    Step
} from 'react-joyride';
import { Styles } from 'react-joyride/types';
import { vars } from '../../theme';
import { applyOpacity } from '../../utils/color.utils';
import { isFunction, isObject } from '../../utils/shared.utils';
import { createPortal } from 'react-dom';
import { isNumber } from 'lodash-es';

/** Extends react-joyride Step with HoneyBook-specific properties used in custom tooltips and isFilesAndFlows mode. */
export interface HBStep extends Omit<Step, 'content' | 'target'> {
    /** Tour step body content. Optional when using a custom tooltipComponent. */
    content?: Step['content'];
    /** CSS selector or HTMLElement to attach the step to. Optional when using a custom tooltipComponent with no spotlight. */
    target?: Step['target'];
    name?: string;
    onAfterStep?: (action: string) => void;
    stepCounter?: number;
    CtaButtonText?: string;
}

const defaultStyles = {
    overlay: {
        backgroundColor: applyOpacity(vars.colors.surface.container, 85)
    }
};

export interface Props {
    /** Identifier for this tour instance, forwarded to lifecycle callbacks via the runTourObject. */
    name?: string;
    /** When true, renders the tour via a portal to document.body to escape stacking contexts. */
    isPortal?: boolean;
    /** Controls tour visibility; pass an object to attach metadata forwarded to lifecycle callbacks. */
    runTour: boolean | object;
    /** Called with null when the tour finishes or is skipped to reset the run state. */
    setRunTour: Function;
    /** Array of react-joyride Step objects defining each tour stop's target, content, and placement. */
    steps?: HBStep[];
    /** react-joyride Styles override, merged with the default overlay styling. */
    styles?: Styles;
    /** Called when the tour begins (first step becomes ready). Receives the runTour metadata object. */
    onStart?: Function;
    /** Called when the user completes all steps. Receives the runTour metadata object. */
    onFinish?: Function;
    /** Called when the user dismisses the tour before completing it. Receives the runTour metadata object. */
    onSkipped?: Function;
    /** Called each time a step's tooltip becomes visible. */
    onVisitStep?: Function;
    /** Called when a step with name 'TRANSITION_STEP' is encountered, enabling async transitions between steps. */
    onTransitionStep?: Function;
    /** DOM element ID whose scrollTop is saved on tour start and restored on finish/skip. */
    containerIdToScrollBack?: string;
    /** Enables Files & Flows mode which injects stepCounter and CTA text overrides into specific steps. */
    isFilesAndFlows?: boolean;
    /** When true, prevents react-joyride from auto-scrolling the page to bring tour targets into view. */
    disableScrolling?: boolean;
    /** Pixel offset from the target element when auto-scrolling to a step. */
    scrollOffset?: number;
    /** Enables controlled mode when provided; the parent manages which step is active. */
    stepIndex?: number;
}

const HBTour = ({
    isPortal,
    runTour,
    setRunTour,
    steps,
    styles,
    onStart,
    onFinish,
    onSkipped,
    onVisitStep,
    onTransitionStep,
    containerIdToScrollBack,
    isFilesAndFlows,
    disableScrolling,
    scrollOffset,
    stepIndex
}: Props) => {
    const [originalScrollTop, setOriginalScrollTop] = useState(0);
    let clonedSteps = null;
    const scrollBackContainerToOriginal = () => {
        if (containerIdToScrollBack) {
            const element = document.querySelector(
                `#${containerIdToScrollBack}`
            );
            if (element) {
                element.scrollTop = originalScrollTop;
            }
        }
    };

    const [stepIndexState, setStepIndexState] = useState(stepIndex);
    const isControlledMode = isNumber(stepIndex);

    const handleJoyrideCallback = data => {
        const { status, action, index, lifecycle, step = {}, type } = data;
        const { name: stepName } = step;
        const runTourObject = isObject(runTour)
            ? { ...(runTour as object), stepName }
            : { stepName };
        if (isFunction(onTransitionStep) && stepName === 'TRANSITION_STEP') {
            onTransitionStep(data);
        }

        // On tour start
        if (
            action === ACTIONS.START &&
            lifecycle === LIFECYCLE.READY &&
            isFunction(onStart)
        ) {
            if (containerIdToScrollBack) {
                const element = document.querySelector(
                    `#${containerIdToScrollBack}`
                );
                if (element) {
                    setOriginalScrollTop(element.scrollTop);
                }
            }
            onStart(runTourObject);
        }

        if (
            action === ACTIONS.UPDATE &&
            lifecycle === LIFECYCLE.TOOLTIP &&
            isFunction(onVisitStep)
        ) {
            onVisitStep(runTourObject);
        }

        // On tour finish
        if (
            status === STATUS.FINISHED &&
            (lifecycle === LIFECYCLE.COMPLETE || isControlledMode)
        ) {
            if (isFunction(onFinish)) {
                onFinish(runTourObject);
            }
            scrollBackContainerToOriginal();
            setRunTour(null);
        }

        // On tour skip
        if (
            status === STATUS.SKIPPED &&
            (lifecycle === LIFECYCLE.COMPLETE || isControlledMode)
        ) {
            if (isFunction(onSkipped)) {
                onSkipped(runTourObject);
            }
            scrollBackContainerToOriginal();
            setRunTour(null);
        }

        if (isControlledMode) {
            if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
                const nextStepIndex =
                    index + (action === ACTIONS.PREV ? -1 : 1);

                if (step.onAfterStep && type === 'step:after') {
                    step.onAfterStep(action);

                    setTimeout(() => {
                        setStepIndexState(nextStepIndex);
                    }, 0);
                } else {
                    setStepIndexState(nextStepIndex);
                }
            }

            if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
                // Need to set our running state to false, so we can restart if we click start again.
                setRunTour(null);
                setStepIndexState(0);
            }
        }
    };

    if (isFilesAndFlows) {
        clonedSteps = steps.map(step => {
            if (
                step.name === 'NEXT_SHARE_BUTTON' ||
                step.name === 'USE_TEMPLATE_BUTTON' ||
                step.name === 'UPDATE_TEMPLATE_BUTTON' ||
                step.name === 'PUBLISH_CHANGES_BUTTON'
            ) {
                return { ...step, stepCounter: 8, CtaButtonText: 'Next' };
            }
            return { ...step, stepCounter: 8 };
        });
    }

    const joyrideElement = (
        <Joyride
            disableScrollParentFix
            disableScrolling={!!disableScrolling}
            continuous={false}
            run={!!runTour}
            steps={clonedSteps || steps}
            callback={handleJoyrideCallback}
            styles={{ ...defaultStyles, ...styles }}
            disableOverlayClose
            disableCloseOnEsc
            scrollOffset={scrollOffset}
            stepIndex={stepIndexState}
        />
    );

    return isPortal
        ? createPortal(joyrideElement, document.querySelector('body'))
        : joyrideElement;
};

export default HBTour;
